import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Download, ShieldCheck, Check, X, FileText, Link2, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "savi_admin_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700",
  created: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
};

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkAmount, setLinkAmount] = useState("");
  const [linkPlan, setLinkPlan] = useState("Software Consultation");

  const generatedLink = linkAmount && parseFloat(linkAmount) > 0
    ? `${window.location.origin}/checkout?plan=${encodeURIComponent(linkPlan || "Software Consultation")}&amount=${encodeURIComponent(parseFloat(linkAmount).toFixed(2))}`
    : "";

  const copyLink = async () => {
    if (!generatedLink) { toast.error("Enter an amount first"); return; }
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success("Payment link copied!");
    } catch {
      toast.error("Could not copy. Select and copy manually.");
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPayments([]);
    setSummary(null);
  }, []);

  const fetchPayments = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payments`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setPayments(data.payments);
      setSummary(data.summary);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not load payments");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchPayments(token);
  }, [token, fetchPayments]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back, admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const openEvidence = (p) => {
    const money = `${p.currency === "INR" ? "₹" : "$"}${Number(p.amount).toFixed(2)} ${p.currency || "USD"}`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Payment Evidence - ${p.order_id}</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif;color:#0F172A}
        body{max-width:760px;margin:40px auto;padding:0 24px}
        h1{color:#0F172A;font-size:22px;margin:0}
        .sub{color:#475569;font-size:12px;margin-top:4px}
        .bar{height:4px;background:#3B82F6;margin:16px 0 24px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        td{padding:10px 8px;border-bottom:1px solid #E2E8F0;font-size:14px;vertical-align:top}
        td.k{color:#475569;width:220px;font-weight:bold}
        .box{background:#F5F7FC;border:1px solid #E2E8F0;border-radius:8px;padding:16px;margin-top:24px;font-size:13px;color:#334155;line-height:1.6}
        .ok{color:#0a7d33;font-weight:bold}
        .foot{margin-top:28px;font-size:11px;color:#94A3B8}
      </style></head><body>
      <h1>Radio App Consultancy — Payment & Consent Evidence</h1>
      <div class="sub">Independent Consultancy · akarjagdev@gmail.com · (864) 481-0776</div>
      <div class="bar"></div>
      <table>
        <tr><td class="k">Customer Name</td><td>${p.name || "—"}</td></tr>
        <tr><td class="k">Email</td><td>${p.email || "—"}</td></tr>
        <tr><td class="k">Phone</td><td>${p.contact || "—"}</td></tr>
        <tr><td class="k">Plan / Item</td><td>${p.plan || "—"}</td></tr>
        <tr><td class="k">Amount Charged</td><td>${money}</td></tr>
        <tr><td class="k">Status</td><td style="text-transform:capitalize">${p.status || "—"}</td></tr>
        <tr><td class="k">Razorpay Payment ID</td><td>${p.payment_id || "—"}</td></tr>
        <tr><td class="k">Razorpay Order ID</td><td>${p.order_id || "—"}</td></tr>
        <tr><td class="k">Order Date/Time (UTC)</td><td>${p.created_at || "—"}</td></tr>
        <tr><td class="k">Customer Consent</td><td>${p.consent ? '<span class="ok">AGREED to Terms of Service &amp; Refund Policy</span>' : "Not recorded"}</td></tr>
        <tr><td class="k">Consent Timestamp (UTC)</td><td>${p.consent_at || "—"}</td></tr>
        <tr><td class="k">Post-Payment Confirmation</td><td>${p.confirmed ? '<span class="ok">CONFIRMED — customer re-authorized this payment after paying</span>' : "Not confirmed"}</td></tr>
        <tr><td class="k">Confirmation Timestamp (UTC)</td><td>${p.confirmed_at || "—"}</td></tr>
        <tr><td class="k">Customer IP Address</td><td>${p.ip_address || "—"}</td></tr>
        <tr><td class="k">Browser / Device</td><td style="font-size:12px;word-break:break-word">${p.user_agent || "—"}</td></tr>
      </table>
      <div class="box">
        <strong>Merchant statement:</strong> The customer above voluntarily initiated a one-time payment on our website,
        explicitly agreed to our Terms of Service and Refund Policy (consent recorded with the timestamp shown), and
        authorized this charge before payment. The customer's IP address and browser/device signature were captured at
        the time of the transaction (shown above) as additional proof of the customer's identity and intent. The payment
        was processed securely via Razorpay with signature verification.
        This document is provided as evidence in response to any dispute/chargeback.
      </div>
      <div class="foot">Generated on ${new Date().toISOString()} · Radio App Consultancy</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to open the evidence sheet"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const exportCSV = () => {
    const cols = ["created_at", "name", "email", "contact", "plan", "amount", "currency", "status", "consent", "consent_at", "confirmed", "confirmed_at", "ip_address", "user_agent", "payment_id", "order_id"];
    const header = cols.join(",");
    const rows = payments.map((p) =>
      cols.map((c) => `"${String(p[c] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `savi-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------------- Login screen ----------------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#26395F] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={handleLogin} className="w-full max-w-md rounded-2xl bg-white p-10 shadow-2xl">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-lg bg-[#3B82F6] flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-[#0F172A]" />
            </span>
            <span className="font-extrabold text-lg tracking-tight text-[#0F172A]">Radio App Consultancy Admin</span>
          </div>
          <h1 className="font-display text-2xl tracking-tight uppercase mt-6 text-[#0F172A]">Admin Login</h1>
          <p className="text-sm text-[#475569] mt-1">Sign in to view payments & consent records.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm text-[#475569]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="akarjagdev@gmail.com"
                className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors text-[#0F172A]"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="text-sm text-[#475569]">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors text-[#0F172A]"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loggingIn}
            className="btn-sweep mt-7 w-full bg-[#26395F] text-white font-semibold py-3.5 rounded-full hover:bg-[#3B82F6] hover:text-[#0F172A] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            <Lock className="h-4 w-4" /> {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---------------- Dashboard ----------------
  return (
    <div className="min-h-screen bg-[#F5F7FC]" data-testid="admin-dashboard">
      <header className="bg-[#26395F] text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-lg bg-[#3B82F6] flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-[#0F172A]" />
            </span>
            <span className="font-extrabold tracking-tight">Radio App Consultancy Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchPayments(token)} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={exportCSV} className="flex items-center gap-2 text-sm bg-[#3B82F6] text-[#0F172A] font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity" data-testid="admin-export-btn">
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl tracking-tight uppercase text-[#0F172A]">Payments & Consent Records</h1>
        <p className="text-sm text-[#475569] mt-1">Use these records as evidence for Razorpay chargeback disputes.</p>

        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="rounded-xl bg-white border border-[#E2E8F0] p-5" data-testid="stat-total">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Total Orders</p>
              <p className="font-display text-3xl text-[#0F172A] mt-1">{summary.total}</p>
            </div>
            <div className="rounded-xl bg-white border border-[#E2E8F0] p-5" data-testid="stat-paid">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Paid</p>
              <p className="font-display text-3xl text-[#0F172A] mt-1">{summary.paid}</p>
            </div>
            <div className="rounded-xl bg-white border border-[#E2E8F0] p-5" data-testid="stat-revenue">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Collected (paid)</p>
              <p className="font-display text-3xl text-[#3B82F6] mt-1">${summary.total_paid_amount}</p>
            </div>
          </div>
        )}

        {/* Payment Link Generator */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E2E8F0] p-6" data-testid="link-generator">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[#3B82F6]" />
            <h2 className="font-display text-lg text-[#0F172A]">Create Payment Link</h2>
          </div>
          <p className="text-sm text-[#475569] mt-1">Set an amount, copy the link, and send it to your customer. They just fill their details and pay.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Amount (USD)</label>
              <input
                type="number" min="1" step="0.01" value={linkAmount}
                onChange={(e) => setLinkAmount(e.target.value)}
                placeholder="299.99"
                className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-2.5 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors text-[#0F172A]"
                data-testid="link-amount-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Label / Plan</label>
              <input
                type="text" value={linkPlan}
                onChange={(e) => setLinkPlan(e.target.value)}
                placeholder="Software Consultation"
                className="mt-2 w-full rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-2.5 outline-none focus:border-[#3B82F6] focus:bg-white transition-colors text-[#0F172A]"
                data-testid="link-plan-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={copyLink}
                className="w-full flex items-center justify-center gap-2 text-sm bg-[#26395F] text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-[#3B82F6] hover:text-[#0F172A] transition-colors"
                data-testid="link-copy-btn"
              >
                <Copy className="h-4 w-4" /> Copy Link
              </button>
            </div>
          </div>
          {generatedLink && (
            <div className="mt-4 rounded-lg bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-3 font-mono text-xs text-[#0F172A] break-all" data-testid="link-preview">
              {generatedLink}
            </div>
          )}
        </div>

        <div className="mt-8 rounded-2xl bg-white border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="text-left text-[#475569] bg-[#F5F7FC]">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Consent</th>
                  <th className="px-4 py-3 font-semibold">Confirmed</th>
                  <th className="px-4 py-3 font-semibold">Payment / Order ID</th>
                  <th className="px-4 py-3 font-semibold">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && !loading && (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-[#94A3B8]">No payments yet.</td></tr>
                )}
                {payments.map((p, i) => (
                  <tr key={p.order_id || i} className="border-t border-[#E2E8F0] hover:bg-[#F5F7FC]/60" data-testid={`payment-row-${i}`}>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{p.name || "—"}</div>
                      <div className="text-xs text-[#475569]">{p.email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{p.contact || "—"}</td>
                    <td className="px-4 py-3 text-[#0F172A]">{p.plan || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A] whitespace-nowrap">{p.currency === "INR" ? "₹" : "$"}{Number(p.amount).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[p.status] || "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {p.consent ? (
                        <span className="inline-flex items-center gap-1 text-green-700" title={p.consent_at ? `Agreed: ${fmtDate(p.consent_at)}` : "Agreed"}>
                          <Check className="h-4 w-4" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#94A3B8]"><X className="h-4 w-4" /> No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.confirmed ? (
                        <span className="inline-flex items-center gap-1 text-green-700" title={p.confirmed_at ? `Confirmed: ${fmtDate(p.confirmed_at)}` : "Confirmed"}>
                          <Check className="h-4 w-4" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#94A3B8]"><X className="h-4 w-4" /> No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#475569]">
                      <div>{p.payment_id || "—"}</div>
                      <div className="text-[#A6ACC2]">{p.order_id}</div>
                      {p.ip_address && <div className="text-[#A6ACC2] mt-1" title={p.user_agent || ""}>IP: {p.ip_address}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openEvidence(p)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B82F6] hover:text-[#0F172A] border border-[#3B82F6]/40 hover:border-[#0F172A] px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                        data-testid={`evidence-btn-${i}`}
                      >
                        <FileText className="h-3.5 w-3.5" /> Evidence
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
